import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData } from 'react-router';

import type { Route } from './+types/root';
import type { PublicEnv } from './config.server';

import './app.css';

import { GeneralErrorBoundary } from './components/error-boundary';
import { MobileCallCta } from './components/layout/mobile-call-cta';
import { SiteFooter } from './components/layout/site-footer';
import { SiteHeader } from './components/layout/site-header';
import { ProgressBar } from './components/progress-bar';
import { href as iconsHref } from './components/ui/icon.tsx';
import { TooltipProvider } from './components/ui/tooltip';
import { getPublicEnv } from './config.server';
import { useNonce } from './providers/nonce-provider';
import { absoluteUrl, buildRealEstateAgentSchema, ldJsonMeta } from './utils/schema';

export const links: Route.LinksFunction = () => [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/static/favicons/favicon-96x96.png',
  },
  { rel: 'apple-touch-icon', href: '/static/favicons/apple-touch-icon.png' },
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials',
  } as const, // necessary to make typescript happy
  { rel: 'icon', type: 'image/svg+xml', href: '/static/favicons/favicon.svg' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400&display=swap',
  },
];

export const meta: Route.MetaFunction = () => {
  return [
    { title: 'Meet Real Estate' },
    { property: 'og:image', content: absoluteUrl('/static/og-image.png') },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    ldJsonMeta(buildRealEstateAgentSchema()),
  ];
};

export async function loader({}: Route.LoaderArgs) {
  return {
    ENV: getPublicEnv(),
  };
}

function Document({
  children,
  nonce,
  env,
  allowIndexing = true,
}: {
  children: React.ReactNode;
  nonce: string;
  env?: PublicEnv;
  allowIndexing?: boolean;
}) {
  return (
    <html lang="en" className="font-sans text-zinc-600 antialiased">
      <head>
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta name="theme-color" content="#0B1B30" />
        {allowIndexing ? null : <meta name="robots" content="noindex, nofollow" />}
        <Links />
      </head>

      <body className="relative isolate flex min-h-svh flex-col">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env ?? {})}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

function App() {
  const { ENV } = useLoaderData<typeof loader>();
  const nonce = useNonce();

  return (
    <Document nonce={nonce} env={ENV}>
      <TooltipProvider delayDuration={250}>
        <SiteHeader />
        <Outlet />
        <SiteFooter
          tagline="Showing up to serve"
          location="Kingaroy & the South Burnett"
          copyright="© 2026 Meet Real Estate. All rights reserved."
        />
        <MobileCallCta />
      </TooltipProvider>
      <ProgressBar />
    </Document>
  );
}

export default App;

export function ErrorBoundary() {
  const nonce = useNonce();
  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  );
}
